import * as React from 'react'
import {Lang} from 'yougile-lang'
import {ArrUtil, Util} from 'yougile-util'
import {
  createDataObjectId,
  createEmptyReport,
  DataObjectId,
  Report,
  reportsContainerId,
  TaskData
} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Loader} from '@components/common/base/loader'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {DepartmentsClient} from '../../../client/departments-client'
import {PermissionsChecker} from '../../../client/permissions-checker'
import {ReportsClient} from '../../../client/reports-client'
import {Sync} from '../../../client/sync'
import {
  noPropagate,
  useReportTasksIds,
  useTasksDataFromIds
} from '../../../containers/hooks'
import {withUpdaters} from '../../../containers/updaters'
import {SyncUpdater} from '../../../containers/updaters/sync-updater'
import {globals} from '../../../extensions/globals-base'
import {Help} from '../../common/help/help'
import {StatsGant} from './stats-gant'
import {StatsReportsEdit} from './stats-reports-edit'
import {
  downloadDashboardXlsx,
  getAllReportTasks
} from './stats-reports-get-tasks'
import {StatsReportsTable} from './stats-reports-table'

interface Props {
  admins: DataObjectId[]
  managers: DataObjectId[]
  reports: Report[]
  tasks: TaskData[]
  save: (newReport: Report) => void
  deleteReport: (report: Report) => Promise<boolean>
  downloadXlsx: (report: Report) => void
  calcReports: () => Map<DataObjectId, {open: number; completed: number}>
  current: DataObjectId | undefined
  setCurrent: (id: DataObjectId | undefined) => void
}

// eslint-disable-next-line max-lines-per-function
const Dashboard = withUpdaters(
  SyncUpdater({
    filter: '*',
    companyType: 'current',
    debounceTime: 5000
  })
)((
  props: Props & {
    createNew: () => void
    openReport: (id: DataObjectId) => () => void
    edit: (r: Report) => void
  }
) => {
  const {
    reports,
    calcReports,
    createNew,
    openReport,
    deleteReport,
    downloadXlsx,
    edit
  } = props
  const bem = Util.bemFrom('stats-reports')
  const stats = calcReports()
  return (
    <div className={bem('dashboard')}>
      <div className={bem('dashboard-title')}>
        {Lang.msg.report.yourReports}
        <Help name="tablesReport" />
      </div>
      <div className={bem('dashboard-row', {header: true})}>
        <div className={bem('dashboard-c1')}>{Lang.msg.report.reportName}</div>
        <div className={bem('dashboard-c2')}>{Lang.msg.report.openTasks}</div>
        <div className={bem('dashboard-c3')}>
          {Lang.msg.report.completedTasks}
        </div>

        <div className={bem('dashboard-c4')} />
      </div>
      <div>
        {reports.map(report => {
          const del = () => deleteReport(report)
          const sett = () => edit(report)
          const xlsx = () => downloadXlsx(report)
          return (
            <div
              className={bem('dashboard-row')}
              key={report.id}
              onClick={openReport(report.id)}
            >
              <div className={bem('dashboard-c1', {link: true})}>
                <span className={bem('dashboard-item')}>{report.title}</span>
              </div>
              <div className={bem('dashboard-c2')}>
                {stats.get(report.id)?.open ?? 0}
              </div>
              <div className={bem('dashboard-c3')}>
                {stats.get(report.id)?.completed ?? 0}
              </div>
              <div className={bem('dashboard-c4')}>
                <div className={bem('dashboard-actions')} onClick={noPropagate}>
                  <div
                    className={bem('dashboard-action-settings')}
                    onClick={sett}
                  />
                  <div
                    className={bem('dashboard-action-exceldl')}
                    onClick={xlsx}
                  />
                  <div
                    className={bem('dashboard-action-delete')}
                    onClick={del}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className={bem('dashboard-add')} onClick={createNew}>
        {Lang.msg.report.addReport}
      </div>
    </div>
  )
})

// eslint-disable-next-line max-lines-per-function
const StatsReportsBase = (props: Props) => {
  const {
    reports,
    save,
    deleteReport,
    downloadXlsx,
    admins,
    managers,
    current,
    setCurrent,
    tasks
  } = props
  const bem = Util.bemFrom('stats-reports')

  const [view, setView] = React.useState<'table' | 'timeline'>('table')
  const [edited, edit] = React.useState<Report | undefined>(undefined)
  const currentReport = reports.find(r => r.id === current)

  const close = React.useCallback(() => edit(undefined), [edit])
  const del = React.useCallback(async () => {
    return edited ? await deleteReport(edited) : false
  }, [deleteReport, edited?.id])
  const saveReport = React.useCallback(
    (report: Report) => {
      save(report)
      setCurrent(report.id)
    },
    [save, setCurrent]
  )
  const openReport = (id: DataObjectId | undefined) => () => setCurrent(id)
  const createNew = () => edit(createEmptyReport([globals.currentUserId]))
  const saveXlsx = () => currentReport && downloadXlsx(currentReport)
  const editReport = () => edit(currentReport)

  const adminsSaved = React.useMemo(() => admins, [admins.join()])
  const managersSaved = React.useMemo(() => managers, [managers.join()])

  const editedPopup = edited ? (
    <StatsReportsEdit
      initialReport={edited}
      saveReport={saveReport}
      close={close}
      deleteReport={del}
      isStatReport={true}
      admins={adminsSaved}
      managers={managersSaved}
      canEdit={true}
    />
  ) : null
  const toggleView = () => {
    if (view === 'table') {
      Tracking.send(TrackingEvent.reportsSwitchToTheGanttChartView)
    }
    setView(view === 'table' ? 'timeline' : 'table')
  }

  if (!currentReport) {
    return (
      <>
        <Dashboard
          {...props}
          createNew={createNew}
          openReport={openReport}
          edit={edit}
        />
        {editedPopup}
      </>
    )
  }

  const totalNum = tasks.length
  const openNum = tasks.filter(t => !t.completeTime).length
  const completeNum = totalNum - openNum

  return (
    <div className={bem()}>
      <div className={bem('panel')}>
        <div className={bem('back')} onClick={openReport(undefined)}>
          {Lang.msg.report.back}
        </div>
        <div className={bem('actions')}>
          <div className={bem('edit')} onClick={editReport}>
            {Lang.msg.statsEditReport}
          </div>
          <div
            className={bem('toggle-view', {timeline: view === 'timeline'})}
            onClick={toggleView}
          >
            {view === 'table'
              ? Lang.msg.report.showTimeline
              : Lang.msg.report.showTable}
          </div>
          <div className={bem('xlsx')} onClick={saveXlsx}>
            {Lang.msg.statsDownloadXlsx}
          </div>
        </div>
      </div>

      {view === 'table' && (
        <div className={bem('header')}>
          <div className={bem('name')}>{currentReport.title}</div>
          <div className={bem('num')}>
            <div className={bem('num-label')}>
              {Lang.msg.report.totalTasksNum}:
            </div>
            <div className={bem('num-value', {total: true})}>{totalNum}</div>
          </div>
          <div className={bem('num')}>
            <div className={bem('num-label')}>
              {Lang.msg.report.openTasksNum}:
            </div>
            <div className={bem('num-value', {open: true})}>{openNum}</div>
          </div>
          <div className={bem('num')}>
            <div className={bem('num-label')}>
              {Lang.msg.report.completedTasksNum}:
            </div>
            <div className={bem('num-value', {complete: true})}>
              {completeNum}
            </div>
          </div>
        </div>
      )}

      {view === 'table' ? (
        <StatsReportsTable
          update={updater => save(updater(currentReport))}
          report={currentReport}
        />
      ) : (
        <StatsGant
          column={currentReport.columns[0]}
          title={currentReport.title}
        />
      )}
      {editedPopup}
    </div>
  )
}

export const StatsReports = withUpdaters(
  SyncUpdater({
    filter: reportsContainerId,
    companyType: 'current',
    debounceTime: 300
  })
)(
  // eslint-disable-next-line max-lines-per-function
  () => {
    const box = Sync.getCurrentSandbox()

    const reportsClient = new ReportsClient(box)
    reportsClient.createExampleReportsIfNeeded()
    const reports = reportsClient.getAllReports()

    if (reports.length === 0) {
      reports.push({
        id: createDataObjectId(),
        title: Lang.msg.unnamed,
        columns: [{title: Lang.msg.unnamed, filter: {}}],
        availableTo: [globals.currentUserId]
      })
    }
    const save = React.useCallback(
      (newReport: Report) => reportsClient.saveReport(newReport),
      []
    )
    const deleteReport = React.useCallback(async (r: Report) => {
      const del = await showDeleteConfirm(Lang.msg.statsDeleteReportConfirm)
      if (del) {
        reportsClient.deleteReport(r.id)
      }
      return del
    }, [])

    const allUsers = box.getAllUsers(false).map(u => u.id)
    const admins = allUsers.filter(uid =>
      PermissionsChecker.isAdmin({userId: uid, box})
    )
    const managers = ArrUtil.unique(
      [
        ...admins,
        ...allUsers.filter(uid => DepartmentsClient.isManager(box, uid))
      ],
      id => id
    )
    const calcReports = React.useCallback(() => {
      const res: Map<DataObjectId, {open: number; completed: number}> =
        new Map()
      reports.forEach(r => {
        const tasks = getAllReportTasks(box, r)
        let open = 0
        let completed = 0
        tasks.forEach(t => {
          if (t.archived) {
            return
          }
          if (t.completeTime) {
            completed++
          } else {
            open++
          }
        })
        res.set(r.id, {open, completed})
      })

      return res
    }, [reports, box])
    const [current, setCurrent] = React.useState<DataObjectId | undefined>(
      undefined
    )
    const currentReport = reports.find(r => r.id === current)
    const {data: tasksIds, isLoading: areTasksLoading} = useReportTasksIds({
      report: currentReport,
      shouldUpdateOnDataChange: true
    })
    const taskData = useTasksDataFromIds(tasksIds)

    if (areTasksLoading) {
      return (
        <div className="flex justify-center">
          <Loader />
        </div>
      )
    }

    return (
      <StatsReportsBase
        reports={reports}
        tasks={taskData}
        current={current}
        setCurrent={setCurrent}
        save={save}
        deleteReport={deleteReport}
        downloadXlsx={downloadDashboardXlsx}
        admins={admins}
        managers={managers}
        calcReports={calcReports}
      />
    )
  }
)
